import React from 'react';
import {
  makeStyles,
  createStyles
} from '@material-ui/core/styles';
import {
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  DialogActions,
  Typography,
  Dialog,
  DialogContent
} from '@material-ui/core';
import { Event, ChevronLeft, ChevronRight } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { loginChecker } from '../../utils/login-controler';

const useStyles = makeStyles(() =>
  createStyles({
    dialogContent: {
      padding: 0
    },
    popoverContent: {
      padding: '0px 12px',
      maxWidth: 290
    },
    yearHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      marginBottom: 8
    },
    yearLabel: {
      minWidth: 80,
      textAlign: 'center',
      fontWeight: 500
    },
    monthsGrid: {
      width: '100%',
      marginBottom: 16
    },
    monthButton: {
      width: '100%',
      borderRadius: 6,
      padding: '6px 0',
      textTransform: 'none'
    }
  })
);

interface Props {
  value: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
}

export const MonthPicker: React.FC<Props> = ({ value, onChange, disabled }) => {
  const history = useHistory();

  const classes = useStyles();
  const today = new Date();
  const initialYear = value ? value.getFullYear() : today.getFullYear();

  const [year, setYear] = React.useState<number>(initialYear);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const [tempMonth, setTempMonth] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (value) setYear(value.getFullYear());
  }, [value]);

  const decreaseYear = () => setYear(y => y - 1);
  const increaseYear = () => setYear(y => y + 1);

  const openPopover = (_e: React.MouseEvent<HTMLElement>) => {
    if (!disabled) setOpenDialog(true);
  };

  React.useEffect(() => {
    if (openDialog) {
      if (value && value.getFullYear() === year) {
        setTempMonth(value.getMonth());
      } else {
        setTempMonth(null);
      }
    }
  }, [openDialog, year, value]);

  const closePopover = () => setOpenDialog(false);

  const handleMonthClick = (m: number) => {
    if (!disabled) setTempMonth(m);
  };

  const handleCancel = () => closePopover();

  const handleOk = () => {
    if (loginChecker(history)) {
      return;
    }
    const selected = tempMonth !== null ? new Date(year, tempMonth, 1) : value;
    onChange(selected);
    closePopover();
  };

  const formatValue = (d: Date | null) => d ? `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, '0')}` : '';

  const months = [
    '1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'
  ];

  return (
    <>
      <TextField
        margin="normal"
        value={formatValue(value)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={openPopover}
                aria-label="open year-month picker"
                aria-haspopup="dialog"
                aria-expanded={openDialog}
                disabled={disabled}
              >
                <Event />
              </IconButton>
            </InputAdornment>
          )
        }}
        disabled={disabled}
      />
      <Dialog
        open={openDialog}
        onClose={closePopover}
      >
        <DialogContent className={classes.dialogContent}>
          <div className={classes.popoverContent}>
            {/* Year navigation header */}
            <div className={classes.yearHeader}>
              <IconButton size="small" onClick={decreaseYear}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="subtitle1" className={classes.yearLabel}>{year}年</Typography>
              <IconButton size="small" onClick={increaseYear}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </div>

            {/* Month grid */}
            <Grid container spacing={1} className={classes.monthsGrid}>
              {months.map((label, idx) => {
                const isSelected = tempMonth !== null ? tempMonth === idx : (value && value.getFullYear() === year && value.getMonth() === idx);
                return (
                  <Grid item xs={3} key={label}>
                    <Button
                      variant={isSelected ? 'contained' : 'text'} // boxで表示なら、'text'から’outlined’に変更
                      color={isSelected ? 'primary' : undefined}
                      className={classes.monthButton}
                      onClick={() => handleMonthClick(idx)}
                      size="small"
                    >
                      {label}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        </DialogContent>

        {/* OK / キャンセル footer */}
        <DialogActions>
          <Button onClick={handleCancel} color="primary">キャンセル</Button>
          <Button onClick={handleOk} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MonthPicker;
